import axios, { AxiosRequestConfig } from 'axios';
import { DashboardStats, TarefaDashboard } from '../../types/dashboard/dashboard.types';

class DashboardService {
  private baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/dashboard`;
  private requestCounter = 0;
  
  // Debug config - apenas em desenvolvimento
  private DEBUG_ENABLED = process.env.NODE_ENV === 'development';

  private log(message: string, data?: any, type: 'info' | 'warn' | 'error' = 'info') {
    if (!this.DEBUG_ENABLED) return;
    
    const emoji = type === 'error' ? '🚨' : type === 'warn' ? '⚠️' : '📊';
    console.log(`${emoji} [Dashboard Service] ${message}`, data || '');
  }

  private getHeaders() {
    const token = localStorage.getItem('token');
    return { 
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    };
  }

  private getClienteId(): string {
    try {
      // 1. Tentar obter do userInfo
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          const parsed = JSON.parse(userInfo);
          if (parsed?.cliente_id) {
            this.log('Cliente ID obtido do userInfo', { cliente_id: parsed.cliente_id });
            return parsed.cliente_id;
          }
        } catch (parseError) {
          this.log('Erro ao parsear userInfo', parseError, 'error');
        }
      }

      // 2. Tentar obter do currentCliente
      const currentCliente = localStorage.getItem('currentCliente');
      if (currentCliente) {
        try {
          const parsed = JSON.parse(currentCliente);
          if (parsed?._id) {
            this.log('Cliente ID obtido do currentCliente', { cliente_id: parsed._id });
            return parsed._id;
          }
        } catch (parseError) {
          this.log('Erro ao parsear currentCliente', parseError, 'error');
        }
      }

      // 3. SessionStorage
      const sessionClienteId = sessionStorage.getItem('cliente_id');
      if (sessionClienteId) {
        this.log('Cliente ID obtido do sessionStorage', { cliente_id: sessionClienteId });
        return sessionClienteId;
      }

      // 4. URL params
      const urlParams = new URLSearchParams(window.location.search);
      const clienteIdFromUrl = urlParams.get('cliente_id');
      if (clienteIdFromUrl) {
        this.log('Cliente ID obtido da URL', { cliente_id: clienteIdFromUrl });
        sessionStorage.setItem('cliente_id', clienteIdFromUrl);
        return clienteIdFromUrl;
      }

      // 5. Valor padrão para desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        const defaultId = '6724cb37ed8cddb316af800e';
        this.log('Usando cliente ID padrão (DEV)', { cliente_id: defaultId }, 'warn');
        return defaultId;
      }

      throw new Error('Cliente ID não encontrado');
    } catch (error) {
      this.log('Erro crítico ao obter cliente ID', error, 'error');
      throw error;
    }
  }

  private async makeRequest<T>(
    method: 'GET' | 'POST',
    endpoint: string,
    params?: Record<string, any>,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    this.requestCounter++;
    const requestId = `REQ_${this.requestCounter}_${Date.now()}`;
    const startTime = Date.now();

    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = queryParams.toString() 
      ? `${this.baseUrl}${endpoint}?${queryParams}` 
      : `${this.baseUrl}${endpoint}`;
    
    this.log(`${method} Request iniciada`, {
      requestId,
      method,
      endpoint,
      url: this.DEBUG_ENABLED ? url : endpoint,
      hasParams: !!params,
      hasData: !!data
    });

    try {
      const axiosConfig: AxiosRequestConfig = {
        method,
        url,
        headers: this.getHeaders(),
        timeout: 20000, // 20 segundos para requests consolidados
        ...config
      };

      if (data) {
        axiosConfig.data = data;
      }

      const response = await axios(axiosConfig);
      const responseTime = Date.now() - startTime;

      this.log(`${method} Response recebida`, {
        requestId,
        endpoint,
        status: response.status,
        responseTime: `${responseTime}ms`,
        dataSize: JSON.stringify(response.data).length
      });

      return response.data;

    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      this.log(`${method} Request falhou`, {
        requestId,
        endpoint,
        responseTime: `${responseTime}ms`,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        status: axios.isAxiosError(error) ? error.response?.status : 'N/A'
      }, 'error');
      
      throw error;
    }
  }

  /**
   * Obtém dados consolidados do dashboard (CPPD + Fornecedores)
   */
  async getDashboardConsolidado(clienteId?: string): Promise<DashboardStats> {
    this.log('getDashboardConsolidado iniciado', {
      clienteId: clienteId ? 'fornecido' : 'será buscado automaticamente'
    });

    try {
      const cliente_id = clienteId || this.getClienteId();
      
      const response = await this.makeRequest<any>('GET', '/consolidado', {
        cliente_id
      });

      // Validar e normalizar a resposta
      if (!response || typeof response !== 'object') {
        throw new Error('Resposta inválida do servidor');
      }

      const dashboardStats: DashboardStats = {
        cppd: {
          totalCPPDs: response.cppd?.totalCPPDs || 0,
          totalReunioes: response.cppd?.totalReunioes || 0,
          reunioesPendentes: response.cppd?.reunioesPendentes || 0,
          reunioesEmAndamento: response.cppd?.reunioesEmAndamento || 0,
          reunioesConcluidas: response.cppd?.reunioesConcluidas || 0,
          proximasReunioes: response.cppd?.proximasReunioes || [],
          membrosCount: response.cppd?.membrosCount || 0,
          estatisticasParticipacao: response.cppd?.estatisticasParticipacao || {
            mediaTaxaParticipacao: 0,
            mediaConvidados: 0,
            mediaPresentes: 0
          }
        },
        fornecedores: {
          totalCadastros: response.fornecedores?.totalCadastros || 0,
          questionariosEnviados: response.fornecedores?.questionariosEnviados || 0,
          questionariosRespondidos: response.fornecedores?.questionariosRespondidos || 0,
          enviosPendentes: response.fornecedores?.enviosPendentes || 0,
          planosDeAcao: response.fornecedores?.planosDeAcao || 0,
          questionariosNaoRespondidos: response.fornecedores?.questionariosNaoRespondidos || 0,
          percentualMaturidade: response.fornecedores?.percentualMaturidade || 0,
          nivelConformidade: response.fornecedores?.nivelConformidade || 'baixo'
        }
      };

      this.log('getDashboardConsolidado concluído', {
        cliente_id,
        success: true,
        cppd: {
          totalCPPDs: dashboardStats.cppd.totalCPPDs,
          totalReunioes: dashboardStats.cppd.totalReunioes
        },
        fornecedores: {
          totalCadastros: dashboardStats.fornecedores.totalCadastros,
          maturidade: dashboardStats.fornecedores.percentualMaturidade
        }
      });

      return dashboardStats;

    } catch (error) {
      this.log('getDashboardConsolidado falhou', {
        clienteId,
        error: error instanceof Error ? error.message : error
      }, 'error');
      throw error;
    }
  }

  /**
   * Obtém tarefas específicas do usuário logado
   */
  async getMinhasTarefas(userId?: string, limit?: number, clienteId?: string): Promise<TarefaDashboard[]> {
    this.log('getMinhasTarefas iniciado', {
      userId: userId ? 'fornecido' : 'será buscado do contexto',
      limit: limit || 10
    });

    try {
      const cliente_id = clienteId || this.getClienteId();
      
      const response = await this.makeRequest<{ tarefas: TarefaDashboard[] }>('GET', '/minhas-tarefas', {
        cliente_id,
        user_id: userId,
        limit: limit || 10
      });

      const tarefas = response.tarefas || [];

      this.log('getMinhasTarefas concluído', {
        cliente_id,
        userId,
        totalTarefas: tarefas.length,
        success: true
      });

      return tarefas;

    } catch (error) {
      this.log('getMinhasTarefas falhou', {
        userId,
        clienteId,
        error: error instanceof Error ? error.message : error
      }, 'error');
      throw error;
    }
  }

  /**
   * Obtém estatísticas rápidas para widgets
   */
  async getQuickStats(clienteId?: string): Promise<{
    cppd: {
      totalCPPDs: number;
      totalReunioes: number;
      reunioesPendentes: number;
      membrosAtivos: number;
    };
    fornecedores: {
      totalFornecedores: number;
      questionariosRespondidos: number;
      percentualRespostas: number;
    };
  }> {
    this.log('getQuickStats iniciado');

    try {
      const cliente_id = clienteId || this.getClienteId();
      
      const response = await this.makeRequest<any>('GET', '/quick-stats', {
        cliente_id
      });

      const quickStats = {
        cppd: {
          totalCPPDs: response.cppd?.totalCPPDs || 0,
          totalReunioes: response.cppd?.totalReunioes || 0,
          reunioesPendentes: response.cppd?.reunioesPendentes || 0,
          membrosAtivos: response.cppd?.membrosAtivos || 0
        },
        fornecedores: {
          totalFornecedores: response.fornecedores?.totalFornecedores || 0,
          questionariosRespondidos: response.fornecedores?.questionariosRespondidos || 0,
          percentualRespostas: response.fornecedores?.percentualRespostas || 0
        }
      };

      this.log('getQuickStats concluído', {
        cliente_id,
        success: true,
        stats: quickStats
      });

      return quickStats;

    } catch (error) {
      this.log('getQuickStats falhou', {
        clienteId,
        error: error instanceof Error ? error.message : error
      }, 'error');
      throw error;
    }
  }

  /**
   * Health check do serviço de dashboard
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.makeRequest<{ status: string }>('GET', '/health');
      
      const isHealthy = response.status === 'OK';
      this.log('Health check', { status: response.status, healthy: isHealthy });
      
      return isHealthy;
    } catch (error) {
      this.log('Health check falhou', error, 'error');
      return false;
    }
  }

  /**
   * Valida se um cliente existe e tem acesso aos dados
   */
  async validateCliente(clienteId: string): Promise<boolean> {
    try {
      await this.makeRequest<any>('GET', '/quick-stats', { cliente_id: clienteId });
      this.log('Cliente validado com sucesso', { clienteId });
      return true;
    } catch (error) {
      this.log('Cliente inválido ou sem acesso', { 
        clienteId, 
        error: error instanceof Error ? error.message : error 
      }, 'warn');
      return false;
    }
  }

  /**
   * Obtém estatísticas de uso do serviço (apenas desenvolvimento)
   */
  getServiceStats() {
    if (!this.DEBUG_ENABLED) return null;

    const stats = {
      baseUrl: this.baseUrl,
      totalRequests: this.requestCounter,
      debugEnabled: this.DEBUG_ENABLED,
      environment: process.env.NODE_ENV,
      storageInfo: {
        userInfo: !!localStorage.getItem('userInfo'),
        currentCliente: !!localStorage.getItem('currentCliente'),
        token: !!localStorage.getItem('token'),
        sessionClienteId: !!sessionStorage.getItem('cliente_id')
      }
    };

    this.log('Service Statistics', stats);
    return stats;
  }

  /**
   * Limpa dados de debug
   */
  clearDebugData() {
    this.requestCounter = 0;
    this.log('Debug data cleared');
  }

  /**
   * Testa conectividade geral do dashboard
   */
  async testConnectivity(): Promise<{
    dashboard: boolean;
    cppd: boolean;
    fornecedores: boolean;
  }> {
    const results = {
      dashboard: false,
      cppd: false,
      fornecedores: false
    };

    try {
      // Testar dashboard consolidado
      results.dashboard = await this.healthCheck();
    } catch (error) {
      this.log('Teste dashboard falhou', error, 'error');
    }

    try {
      // Testar CPPD (usando import dinâmico para evitar dependência circular)
      const CPPDService = await import('./cppd.service');
      results.cppd = await CPPDService.default.healthCheck();
    } catch (error) {
      this.log('Teste CPPD falhou', error, 'error');
    }

    try {
      // Testar Fornecedores (usando quick stats como teste)
      const FornecedoresService = await import('./fornecedores.service');
      const clienteId = this.getClienteId();
      await FornecedoresService.default.getStats(clienteId);
      results.fornecedores = true;
    } catch (error) {
      this.log('Teste Fornecedores falhou', error, 'error');
    }

    this.log('Teste de conectividade concluído', results);
    return results;
  }

  /**
   * Força refresh de todos os dados do dashboard
   */
  async forceRefresh(clienteId?: string): Promise<DashboardStats> {
    this.log('Força refresh iniciado');
    
    try {
      // Limpar possíveis caches
      this.clearDebugData();
      
      // Recarregar dados consolidados
      const dashboardData = await this.getDashboardConsolidado(clienteId);
      
      this.log('Força refresh concluído com sucesso');
      return dashboardData;
    } catch (error) {
      this.log('Força refresh falhou', error, 'error');
      throw error;
    }
  }

  /**
   * Obtém informações sobre a última atualização dos dados
   */
  getLastUpdateInfo(): {
    timestamp: string;
    requestCount: number;
    debugEnabled: boolean;
  } {
    return {
      timestamp: new Date().toISOString(),
      requestCount: this.requestCounter,
      debugEnabled: this.DEBUG_ENABLED
    };
  }

  /**
   * Configura intervalo automático de atualização
   */
  setupAutoRefresh(
    callback: (data: DashboardStats) => void, 
    intervalMinutes: number = 5,
    clienteId?: string
  ): () => void {
    this.log('Configurando auto-refresh', { intervalMinutes });
    
    const interval = setInterval(async () => {
      try {
        this.log('Executando auto-refresh');
        const data = await this.getDashboardConsolidado(clienteId);
        callback(data);
      } catch (error) {
        this.log('Auto-refresh falhou', error, 'error');
      }
    }, intervalMinutes * 60 * 1000);

    // Retorna função para limpar o intervalo
    return () => {
      clearInterval(interval);
      this.log('Auto-refresh cancelado');
    };
  }
}

export default new DashboardService();